import dayjs from "dayjs";

import { DEFAULT_CURRENCY, formatMinorAmount } from "@/core/utils/func/money-minor";
import type {
  ReportingSeriesPointDto,
  ReportingTotalDto,
} from "@/features/reporting/api/dto";
import { findTotal } from "@/features/reporting/api/parse";

const sumByDate = (
  points: ReportingSeriesPointDto[],
  field: "count" | "sumMinor"
): Map<string, number> => {
  const map = new Map<string, number>();
  for (const point of points) {
    if (!point.date) continue;
    map.set(point.date, (map.get(point.date) ?? 0) + (point[field] ?? 0));
  }
  return map;
};

const sortedDates = (...maps: Map<string, number>[]): string[] =>
  [...new Set(maps.flatMap((map) => [...map.keys()]))].sort();

export const formatCount = (value: number | undefined): string =>
  value == null ? "—" : new Intl.NumberFormat().format(value);

export const kpiAmount = (total: ReportingTotalDto | undefined): string =>
  total?.sumMinor != null
    ? formatMinorAmount(total.sumMinor, total.currency ?? DEFAULT_CURRENCY)
    : formatCount(total?.count);

export const kycCasesTotal = (
  totals: ReportingTotalDto[] | undefined
): number | undefined => {
  const opened = findTotal(totals, "kyc.case_opened")?.count;
  const submitted = findTotal(totals, "kyc.case_submitted")?.count;
  const reviewed = findTotal(totals, "kyc.case_reviewed")?.count;
  const values = [opened, submitted, reviewed].filter(
    (value): value is number => value != null
  );
  if (!values.length) return findTotal(totals, "kyc.party_created")?.count;
  return Math.max(...values);
};

export const abandonRate = (
  computed: ReportingTotalDto | undefined,
  collected: ReportingTotalDto | undefined
): string | undefined => {
  const quote = computed?.sumMinor ?? computed?.count;
  const fee = collected?.sumMinor ?? collected?.count;
  if (quote == null || quote <= 0 || fee == null) return undefined;
  const pct = Math.max(0, (1 - fee / quote) * 100);
  return `${pct.toFixed(1)} %`;
};

export const toTransactionChart = (points: ReportingSeriesPointDto[]) => {
  const byDay = sumByDate(points, "count");
  return [...byDay.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, transaction]) => ({
      month: dayjs(date).format("DD"),
      transaction,
    }));
};

export const toRevenueChart = (
  computed: ReportingSeriesPointDto[],
  collected: ReportingSeriesPointDto[]
) => {
  const quotes = sumByDate(computed, "sumMinor");
  const fees = sumByDate(collected, "sumMinor");
  return sortedDates(quotes, fees).map((date) => ({
    month: dayjs(date).format("DD"),
    primary: quotes.get(date) ?? 0,
    secondary: fees.get(date) ?? 0,
  }));
};

export const toFinanceChart = (
  net: ReportingSeriesPointDto[],
  gross: ReportingSeriesPointDto[],
  fee: ReportingSeriesPointDto[]
) => {
  const netMap = sumByDate(net, "sumMinor");
  const grossMap = sumByDate(gross, "sumMinor");
  const feeMap = sumByDate(fee, "sumMinor");
  const dates = sortedDates(netMap, grossMap, feeMap);
  const raw = dates.map((date) => ({
    month: dayjs(date).format("DD"),
    profit: netMap.get(date) ?? 0,
    income: grossMap.get(date) ?? 0,
    expense: feeMap.get(date) ?? 0,
  }));
  const max = Math.max(
    ...raw.flatMap((row) => [row.profit, row.income, row.expense]),
    1
  );
  const factor = max > 50 ? max / 50 : 1;
  return raw.map((row) => ({
    month: row.month,
    profit: row.profit / factor,
    income: row.income / factor,
    expense: row.expense / factor,
  }));
};
